import { getErrorMessage } from "@/utils/error";
import { config } from "@common/utils/config";
import { toast } from "@givenwell/components";
import { createAuth } from "@givenwell/fusionauth";
import { useDebouncedValue } from "@givenwell/hooks";
import {
  AccountOwnerService,
  AirWallexComponentsService,
  AirWallexService,
  AllowanceService,
  ApiError,
  AuthService,
  CampaignsService,
  CategoriesService,
  CategoryService,
  ClaimsService,
  CronofyService,
  DataService,
  LegalTermsService,
  SupplierLegalTermsService,
  SupporterLegalTermsService,
  ListingService,
  LocationService,
  ManagementInvitationService,
  MediaService,
  MemberService,
  MetadataService,
  MgmtMessagesService,
  MgmtPurchaseService,
  MgmtSubscriptionsService,
  NotificationsService,
  OpenAPI,
  PaymentsService,
  PayoutsService,
  ReportingService,
  ResourceService,
  RoleService,
  SalesService,
  SupplierMarketsService,
  SupplierService,
  SupporterCountriesService,
  SupporterEapService,
  SupporterPayInService,
  SupporterReportingService,
  SupporterService,
  SupporterTransactionsService,
  ThemeService,
  UserService,
} from "@givenwell/management-api";
import {
  DefaultError,
  hashKey,
  QueryCache,
  QueryClient,
  QueryKey,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { useCallback, useState } from "react";

export const auth = createAuth({
  serverUrl: config.apiBaseUrl,
  redirectUri: config.manageUrl,
  logoutUri: config.manageUrl + "/sign-in",
  environmentName: config.environmentName,
  applicationName: "management",
});
OpenAPI.BASE = config.apiBaseUrl;
OpenAPI.TOKEN = auth.getAccessToken;
// The API and the SPA are on different origins, so the browser only sends cookies when the request
// opts in. Needed for the httpOnly "trust this device" cookie to reach the login endpoints.
OpenAPI.WITH_CREDENTIALS = true;

export const api = {
  accountOwner: AccountOwnerService,
  airWallex: AirWallexService,
  airWallexComponents: AirWallexComponentsService,
  allowance: AllowanceService,
  auth: AuthService,
  claims: ClaimsService,
  campaigns: CampaignsService,
  category: CategoryService,
  categories: CategoriesService,
  cronofy: CronofyService,
  data: DataService,
  invitation: ManagementInvitationService,
  legalTerms: LegalTermsService,
  supplierLegalTerms: SupplierLegalTermsService,
  supporterLegalTerms: SupporterLegalTermsService,
  listings: ListingService,
  location: LocationService,
  media: MediaService,
  member: MemberService,
  metadata: MetadataService,
  mgmtMessages: MgmtMessagesService,
  mgmtPurchase: MgmtPurchaseService,
  notifications: NotificationsService,
  payments: PaymentsService,
  payouts: PayoutsService,
  resources: ResourceService,
  reporting: ReportingService,
  roles: RoleService,
  sales: SalesService,
  subscriptions: MgmtSubscriptionsService,
  supplier: SupplierService,
  supplierMarkets: SupplierMarketsService,
  supporter: SupporterService,
  supporterCountries: SupporterCountriesService,
  supporterEap: SupporterEapService,
  supporterTransactions: SupporterTransactionsService,
  supporterPayIn: SupporterPayInService,
  supporterReporting: SupporterReportingService,
  user: UserService,
  theme: ThemeService,
};

let refetchTokenStatus: "running" | "idle" = "idle";

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError(error) {
      if (error instanceof ApiError && error.status === 401) {
        if (refetchTokenStatus === "running") {
          return;
        }
        refetchTokenStatus = "running";
        auth
          .renewAuthTokens(true)
          .then(success => {
            if (success) {
              setTimeout(() => {
                api.auth
                  .getManagementUserInfo()
                  .then(() => {
                    queryClient.refetchQueries();
                  })
                  .catch(() => {
                    auth.reset();
                    auth.redirectToLogin();
                  });
              }, 100);
            } else {
              auth.reset();
              auth.redirectToLogin();
            }
          })
          .finally(() => {
            // 30 sec grace period from attempting to refresh token before allowing another attempt.
            setTimeout(() => {
              refetchTokenStatus = "idle";
            }, 30 * 1000);
          });
      }
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      onSuccess() {
        queryClient.invalidateQueries();
      },
      onError(error) {
        const errorMessage = getErrorMessage(error);
        toast.error("Something went wrong", {
          description: errorMessage,
        });
      },
    },
  },
});

export type SimpleUseQueryOptions<TData> = Partial<UseQueryOptions<TData, Error, TData, string[]>>;

export type FetchMethod = "CONNECT" | "DELETE" | "GET" | "HEAD" | "OPTIONS" | "PATCH" | "POST" | "PUT" | "TRACE";

export type FetchOptions = {
  method?: FetchMethod;
  headers?: Record<string, string>;
  body?: XMLHttpRequestBodyInit | Document | null | undefined;
  download?: {
    onProgress?: (progress: number) => void;
    onLoadEnd?: () => void;
    onLoadStart?: () => void;
  };
  upload?: {
    onProgress?: (progress: number) => void;
    onLoadEnd?: () => void;
    onLoadStart?: () => void;
  };
};

export function fetchWithProgress<TData>(input: URL | string, options?: FetchOptions) {
  return new Promise<TData>((resolve, reject) => {
    const request = new XMLHttpRequest();

    request.addEventListener("abort", e => {
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      reject(e);
    });

    request.addEventListener("error", e => {
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      reject(e);
    });

    request.addEventListener("load", e => {
      let response: any = null;
      try {
        response = JSON.parse(request.response) as TData;
      } catch {}
      if (request.status >= 200 && request.status < 300) {
        resolve(response);
      }
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      reject(e);
    });

    request.addEventListener("loadstart", () => {
      options?.download?.onProgress?.(0);
      options?.download?.onLoadStart?.();
    });

    request.addEventListener("progress", e => {
      if (e.lengthComputable && e.total > 0) {
        const progress = Math.ceil(e.loaded / e.total);
        options?.download?.onProgress?.(progress);
      }
    });

    request.addEventListener("loadend", () => {
      options?.download?.onProgress?.(1);
      options?.download?.onLoadEnd?.();
    });

    request.upload.addEventListener("loadstart", () => {
      options?.upload?.onProgress?.(0);
      options?.upload?.onLoadStart?.();
    });

    request.upload.addEventListener("progress", e => {
      if (e.lengthComputable && e.total > 0) {
        const progress = Math.ceil(e.loaded / e.total);
        options?.upload?.onProgress?.(progress);
      }
    });

    request.upload.addEventListener("loadend", () => {
      options?.upload?.onProgress?.(1);
      options?.upload?.onLoadEnd?.();
    });

    request.open(options?.method || "GET", input);

    if (options?.headers) {
      Object.entries(options?.headers).forEach(([key, value]) => {
        request.setRequestHeader(key, value);
      });
    }

    request.send(options?.body);
  });
}

/**
 * Like useQuery, but debounces the fetch function while not debouncing if cached.
 */
export function useDebouncedQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(queryOptions: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, debounceMs = 300) {
  const queryClient = useQueryClient();

  const queryKey = useStableQueryKey(queryOptions.queryKey);
  const debouncedQueryKey = useDebouncedValue(queryKey, debounceMs);

  const getUndebouncedDataFromCacheIfExists = useCallback(
    function <T>(data: T) {
      const dataFromCache = queryClient.getQueryData(queryKey);
      if (dataFromCache) {
        return dataFromCache as T;
      }
      return data;
    },
    [queryClient, queryKey],
  );

  return useQuery({
    ...queryOptions,
    queryKey: debouncedQueryKey,
    placeholderData: previousData => previousData,
    select: getUndebouncedDataFromCacheIfExists,
  });
}

/**
 * makes sure the query key is stable between renders
 */
function useStableQueryKey<TQueryKey extends QueryKey = QueryKey>(value: TQueryKey) {
  const [stableValue, setStableValue] = useState(value);
  const [previousQueryKeyHash, setPreviousQueryKeyHash] = useState(hashKey(value));

  const queryKeyHash = hashKey(value);
  if (queryKeyHash !== previousQueryKeyHash) {
    setStableValue(value);
    setPreviousQueryKeyHash(queryKeyHash);
  }

  return stableValue;
}
